import React, { useState, useEffect, useRef } from 'react';
import { navLinks } from '../data/mockData';
import { Menu, X, LogOut, User, Briefcase, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { useModal } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { appName } from '../config/branding';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_BADGE_STYLES = {
  customer:     { label: 'Customer',     className: 'bg-blue-50 text-blue-700 border-blue-200' },
  professional: { label: 'Professional', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  admin:        { label: 'Admin',        className: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const RoleBadges = ({ roles, size = 'sm', testIdPrefix = 'role-badge' }) => {
  if (!roles) return null;
  const active = Object.entries(roles).filter(([, v]) => v);
  if (active.length === 0) return null;
  const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-container`}>
      {active.map(([key]) => {
        const s = ROLE_BADGE_STYLES[key];
        if (!s) return null;
        return (
          <span
            key={key}
            data-testid={`${testIdPrefix}-${key}`}
            className={`inline-flex items-center rounded-full border font-semibold uppercase tracking-wide ${padding} ${s.className}`}
          >
            {s.label}
          </span>
        );
      })}
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [professionalProfile, setProfessionalProfile] = useState(null);
  const [whoami, setWhoami] = useState(null);
  const { openRequestModal } = useModal();
  const { user, login, logout } = useAuth();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if user has professional profile
  useEffect(() => {
    const checkProfessionalProfile = async () => {
      if (user?.user_id) {
        try {
          const res = await axios.get(`${API}/professionals/profile/${user.user_id}`);
          setProfessionalProfile(res.data);
        } catch (e) {
          // Not a professional or not registered yet
          setProfessionalProfile(null);
        }
      }
    };
    checkProfessionalProfile();
  }, [user]);

  // Load role breakdown for role badges
  useEffect(() => {
    const loadWhoami = async () => {
      if (!user?.user_id) {
        setWhoami(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/whoami`, { withCredentials: true });
        setWhoami(res.data);
      } catch {
        setWhoami(null);
      }
    };
    loadWhoami();
  }, [user]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-1 no-underline">
            <span className="text-2xl font-bold text-[#37b34a]">{appName}</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors duration-200 no-underline"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 hover:bg-gray-50 rounded-full py-1.5 pl-1.5 pr-3 transition-colors duration-200"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#37b34a] flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                    {user.name?.split(' ')[0] || 'User'}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate mb-2">{user.email}</p>
                      <RoleBadges roles={whoami?.roles} testIdPrefix="navbar-role-badge" />
                    </div>

                    {/* Dashboard Links */}
                    {professionalProfile ? (
                      <a
                        href={`/dashboard/${professionalProfile.id}/leads`}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 no-underline"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <LayoutDashboard size={16} />
                        Professional Dashboard
                      </a>
                    ) : (
                      <a
                        href="/register-professional"
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#37b34a] hover:bg-green-50 transition-colors duration-150 no-underline"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Briefcase size={16} />
                        Become a Professional
                      </a>
                    )}

                    <a
                      href="/dashboard/customer/requests"
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 no-underline border-t border-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User size={16} />
                      My Requests
                    </a>

                    {whoami?.roles?.admin && (
                      <a
                        href="/admin/status"
                        data-testid="navbar-admin-status-link"
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-purple-700 hover:bg-purple-50 transition-colors duration-150 no-underline border-t border-gray-100"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <ShieldCheck size={16} />
                        Admin Status
                      </a>
                    )}

                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 border-t border-gray-100"
                    >
                      <LogOut size={16} />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={login}
                  className="text-gray-700 hover:text-gray-900 text-sm font-medium px-4 py-2 transition-colors duration-200"
                >
                  Sign in
                </button>
                <a
                  href="/register"
                  className="bg-[#37b34a] hover:bg-[#2d9a3e] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200 no-underline"
                >
                  Register Now
                </a>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-700 hover:text-gray-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block text-gray-700 hover:text-gray-900 text-base font-medium py-2 no-underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <hr className="border-gray-200" />
            {user ? (
              <>
                <div className="flex items-center gap-3 py-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#37b34a] flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
                {whoami?.roles && (
                  <div className="pb-1">
                    <RoleBadges roles={whoami.roles} testIdPrefix="navbar-mobile-role-badge" />
                  </div>
                )}
                
                {/* Dashboard Links */}
                {professionalProfile ? (
                  <a
                    href={`/dashboard/${professionalProfile.id}/leads`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full text-left text-gray-700 hover:text-gray-900 text-base font-medium py-2 flex items-center gap-2 no-underline"
                  >
                    <LayoutDashboard size={16} /> Professional Dashboard
                  </a>
                ) : (
                  <a
                    href="/register-professional"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full text-left text-[#37b34a] hover:text-[#2d9a3e] text-base font-medium py-2 flex items-center gap-2 no-underline"
                  >
                    <Briefcase size={16} /> Become a Professional
                  </a>
                )}
                
                <a
                  href="/dashboard/customer/requests"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-left text-gray-700 hover:text-gray-900 text-base font-medium py-2 flex items-center gap-2 no-underline"
                >
                  <User size={16} /> My Requests
                </a>

                {whoami?.roles?.admin && (
                  <a
                    href="/admin/status"
                    data-testid="navbar-mobile-admin-status-link"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full text-left text-purple-700 hover:text-purple-800 text-base font-medium py-2 flex items-center gap-2 no-underline"
                  >
                    <ShieldCheck size={16} /> Admin Status
                  </a>
                )}

                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="block w-full text-left text-red-600 text-base font-medium py-2 flex items-center gap-2"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setMobileMenuOpen(false); login(); }}
                  className="block w-full text-left text-gray-700 hover:text-gray-900 text-base font-medium py-2"
                >
                  Sign in
                </button>
                <a
                  href="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-[#37b34a] hover:bg-[#2d9a3e] text-white text-base font-semibold px-5 py-3 rounded-full text-center transition-colors duration-200 no-underline"
                >
                  Register Now
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
